import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAccessToken } from '@dss/auth'
import { ApiError, listPortalCatalog, getPortalCatalogProduct, listPortalCatalogCategories, getPortalCatalogCategoryPath,
  type PortalCatalogItem } from '@dss/api-client'
import { Banner, Button, Card, Input } from '@dss/design-system'
import { api } from '../../lib/api'
import { Link, useLocation, useNavigate } from '../../lib/router'
import { catalogSearchString, parsePortalCatalogLocation } from './portalCatalogLocation'
import { usePortalSession } from '../invitations/usePortalMembership'
import { useCartIdentity, usePortalCartContext } from '../cart/usePortalCartContext'
import { useAddToCart } from '../cart/useCart'
import { useScopedInputDraft } from '../cart/useScopedInputDraft'
import { ClientShell } from '../../components/ClientShell'
import './portal-catalog.css'

export function PortalCatalogScreen({ productId }: { productId?: number }) {
  const token = useAccessToken()
  const identity = useCartIdentity()
  const location = useLocation()
  const [lostIdentity, setLostIdentity] = useState<string | null>(null)
  const [generation, setGeneration] = useState(0)
  const retryScope = useRef({ identity, nonce: 0, alive: true })
  if (retryScope.current.identity !== identity) {
    retryScope.current = { identity, nonce: retryScope.current.nonce + 1, alive: true }
  }
  useEffect(() => {
    retryScope.current.alive = true
    return () => { retryScope.current.alive = false; retryScope.current.nonce += 1 }
  }, [])
  const accessLost = useCallback(() => {
    if (retryScope.current.identity !== identity || !retryScope.current.alive) return
    retryScope.current.nonce += 1
    setLostIdentity(identity)
  }, [identity])
  const session = usePortalSession()
  const context = usePortalCartContext()
  const allowed = !session.isError && session.data?.state === 'ACTIVE'
    && ['CLIENT_OWNER', 'CLIENT_EMPLOYEE'].includes(session.data.role)
    && session.data.permissions.includes('client.catalog.view')
  const accessCheckFailed = (session.isError && !isAccessDenied(session.error))
    || (allowed && context.isError && !isAccessDenied(context.error))
  const retryAccess = () => { void session.refetch(); if (allowed) void context.refetch() }
  const draftPoint = context.selectedPointId
  const checking = session.isPending || context.isLoading
  const retainInput = Boolean(token) && lostIdentity !== identity
    && !isAccessDenied(session.error) && !isAccessDenied(context.error)
    && (allowed || accessCheckFailed || checking)
    && (context.pointId !== null || accessCheckFailed || checking)
  const [quantities, setQuantities] = useScopedInputDraft<Record<number, string>>(
    retainInput ? `${identity}:${draftPoint}:${productId ?? 'list'}:${location.search}` : null, {})
  return <ClientShell active="catalog" commerceContext={Boolean(allowed && !context.isError && lostIdentity !== identity)}>
    <div className="portal-catalog"><h1>{productId ? 'Карточка товара' : 'Каталог для вашей точки'}</h1>
    {session.isPending || context.isLoading ? <p role="status">Проверяем доступ к каталогу…</p>
      : accessCheckFailed ? <><Banner variant="warning"
        body="Не удалось проверить доступ к каталогу. Это может быть временной ошибкой соединения. Прежние товары и условия скрыты." />
        <Button onClick={retryAccess} disabled={session.isFetching || context.isFetching}>Повторить проверку доступа</Button></>
        : !allowed || !token || context.isError ? <><Banner variant="warning"
          body="Каталог недоступен для вашего текущего доступа. Уточните назначенные точки и права у владельца вашей клиентской группы или менеджера дистрибьютора." />
          <Link to="/me">Мои точки и доступ</Link></>
        : lostIdentity === identity ? <><Banner variant="danger" body="Доступ к каталогу был отозван. Категории, товары и условия скрыты." />
          <Button onClick={() => {
            const nonce = ++retryScope.current.nonce
            void Promise.all([session.refetch(), context.refetch()]).then(([membership, points]) => {
              if (retryScope.current.alive && retryScope.current.identity === identity
                && retryScope.current.nonce === nonce && !membership.isError && !points.isError) {
                setLostIdentity(null); setGeneration((value) => value + 1)
              }
            })
          }}>Проверить доступ снова</Button></>
        : <>
          <p className="portal-catalog-lead">Корзина не резервирует товар. Условия проверим перед отправкой.</p>
          {context.data && !context.data.tradepoints.length ? <><Banner variant="info"
            body="Торговые точки для заказа пока не назначены. Посмотрите свой доступ и уточните назначение у владельца вашей клиентской группы или менеджера дистрибьютора." />
            <Link to="/me">Мои точки и доступ</Link></>
            : context.data && !context.data.pairs.length ? <><Banner variant="info"
              body="Для ваших точек пока нет доступного сочетания точки и плательщика. Уточните настройку у менеджера дистрибьютора." />
              <Link to="/me">Мои точки и доступ</Link></>
            : context.pointId === null ? <Banner variant="info" body="Выберите доступную торговую точку, чтобы увидеть её ассортимент и условия." />
            : <Catalog key={`${identity}:${context.pointId}:${productId ?? 'list'}:${generation}`} token={token}
              identity={identity} pointId={context.pointId} productId={productId} locked={context.locked} onAccessLost={accessLost}
              quantities={quantities} onQuantity={(id, value) => setQuantities({ ...quantities, [id]: value })} />}</>}
  </div></ClientShell>
}

function Catalog({ token, identity, pointId, productId, locked, onAccessLost, quantities, onQuantity }: {
  token: string; identity: string; pointId: number; productId?: number; locked: boolean; onAccessLost: () => void
  quantities: Record<number, string>; onQuantity: (id: number, value: string) => void
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const filters = parsePortalCatalogLocation(location.search)
  const { q: search, categoryId } = filters
  const page = productId ? filters.skuPage : filters.page
  const [input, setInput] = useState(search)
  const path = useCategoryPath(token, identity, categoryId, onAccessLost)
  const categoryName = categoryId === undefined ? 'Все категории'
    : path.isError ? 'Путь выбранной категории недоступен' : path.data?.map((entry) => entry.name).join(' / ') ?? 'Загружаем выбранную категорию…'
  useEffect(() => { setInput(search) }, [search])
  const setPage = (value: number) => navigate(location.pathname + catalogSearchString({
    ...filters, ...(productId ? { skuPage: value } : { page: value }),
  }, Boolean(productId)))
  const [added, setAdded] = useState<string | null>(null)
  const add = useAddToCart(pointId)
  const query = useQuery({ queryKey: ['portal-catalog', identity, pointId, productId, search, categoryId, page],
    queryFn: ({ signal }) => productId
      ? getPortalCatalogProduct(api, token, productId, { tradepointId: pointId, page, size: 20 }, signal)
      : listPortalCatalog(api, token, { tradepointId: pointId, q: search, categoryId, page, size: 20 }, signal),
    retry: false, staleTime: 0, refetchInterval: 30_000, refetchOnWindowFocus: 'always' })
  const data = query.isError ? undefined : query.data
  useEffect(() => {
    if ([query.error, add.error].some(isAccessDenied)) onAccessLost()
  }, [query.error, add.error, onAccessLost])
  return <>
    {productId && <Link to={`/catalog${catalogSearchString(filters)}`}>← Вернуться к результатам поиска</Link>}
    {!productId && <section className="portal-catalog-filters" aria-label="Поиск и категория"><form className="portal-catalog-search" onSubmit={(event) => {
      event.preventDefault()
      navigate(`/catalog${catalogSearchString({ ...filters, q: input.trim(), page: 0 })}`)
    }}><label htmlFor="portal-catalog-search">Название или артикул</label>
      <Input id="portal-catalog-search" value={input} maxLength={160} onChange={(event) => setInput(event.target.value)} />
      <Button type="submit">Найти товар</Button></form>
      <CategoryFilter identity={identity} token={token} selectedName={categoryName}
        onAccessLost={onAccessLost}
        onSelect={(id) => {
          navigate(`/catalog${catalogSearchString({ ...filters, categoryId: id, page: 0 })}`)
        }} />
    </section>}
    {categoryId !== undefined && path.isError && <p role="alert">Не удалось восстановить название выбранной категории.
      <Button onClick={() => void path.refetch()}>Повторить загрузку категории</Button></p>}
    {locked && <Banner variant="warning" body="В корзине осталась незавершённая проверка заказа. Завершите её перед изменением состава." />}
    {added && !query.isError && <p role="status">Добавлено в корзину: {added}. <Link to="/cart">Проверить корзину</Link></p>}
    {add.error && <p role="alert">{add.error instanceof ApiError && add.error.status === 409
      ? <>Добавление не подтверждено: товар мог уже появиться в корзине. Прежнее количество не заменено. <Link to="/cart">Проверить корзину</Link></>
      : add.error.message}</p>}
    <div className="portal-catalog-results">
      {data && <p>Склад: {data.warehouseName ?? 'не назначен — обратитесь к менеджеру дистрибьютора'}.
        <span>Найдено позиций: {data.items.totalElements}.</span></p>}
      <Button onClick={() => void query.refetch()} disabled={query.isFetching}>Обновить условия</Button>
    </div>
    {query.isError ? <Banner variant="danger" body="Каталог сейчас недоступен. Прежние цены и остатки скрыты; обновите условия или обратитесь к менеджеру." />
      : query.isPending ? <p role="status">Загружаем товары и условия…</p> : data && <>
        {!data.items.content.length && <>
          <Banner variant="info" body="Доступных товаров по этому запросу нет. Измените поиск или уточните публикацию ассортимента у дистрибьютора." />
          <Button onClick={() => navigate('/catalog')}>Сбросить поиск и категорию</Button>
        </>}
        <div className="portal-catalog-grid">{data.items.content.map((item) => <CatalogItem key={item.skuId}
          quantity={quantities[item.skuId] ?? '1'} onQuantity={value => onQuantity(item.skuId, value)}
          productHref={`/catalog/p/${item.productId}${catalogSearchString(filters)}`}
          item={item} busy={locked || add.isPending || query.isFetching} onAdd={(quantity) => {
            setAdded(null)
            add.mutate({ tradepointId: pointId, skuId: item.skuId, quantity },
              { onSuccess: () => setAdded(item.name) })
          }} />)}</div>
        {(data.items.totalPages > 1 || page > 0) && <div className="portal-catalog-pagination"><Button disabled={page === 0 || query.isFetching} onClick={() => setPage(page - 1)}>Предыдущая страница</Button>
          <span>Страница {page + 1} из {Math.max(1, data.items.totalPages)}</span>
          <Button disabled={page + 1 >= data.items.totalPages || query.isFetching} onClick={() => setPage(page + 1)}>Следующая страница</Button></div>}
      </>}
  </>
}

function useCategoryPath(token: string, identity: string, id: number | undefined, onAccessLost: () => void) {
  const query = useQuery({ queryKey: ['portal-category-path', identity, id], enabled: id !== undefined,
    queryFn: ({ signal }) => getPortalCatalogCategoryPath(api, token, id!, signal), retry: false, staleTime: 0 })
  useEffect(() => { if (isAccessDenied(query.error)) onAccessLost() }, [query.error, onAccessLost])
  return query
}

function CategoryOption({ token, identity, id, onSelect, onAccessLost }: {
  token: string; identity: string; id: number; onSelect: (id: number) => void; onAccessLost: () => void
}) {
  const path = useCategoryPath(token, identity, id, onAccessLost)
  return <li>{path.isError ? <span>Путь категории недоступен. <Button onClick={() => void path.refetch()}>Повторить</Button></span>
    : path.isPending ? <span>Загружаем путь категории…</span>
      : <Button variant="ghost" onClick={() => onSelect(id)}>{path.data.map((entry) => entry.name).join(' / ')}</Button>}</li>
}

function CategoryFilter({ token, identity, selectedName, onSelect, onAccessLost }: { token: string; identity: string;
  selectedName: string; onSelect: (id: number | undefined) => void; onAccessLost: () => void }) {
  const [opened, setOpened] = useState(false)
  const [page, setPage] = useState(0)
  const [input, setInput] = useState('')
  const [search, setSearch] = useState('')
  const categories = useQuery({ queryKey: ['portal-catalog-categories', identity, search, page],
    queryFn: ({ signal }) => listPortalCatalogCategories(api, token, { q: search, page, size: 20 }, signal), retry: false, enabled: opened })
  useEffect(() => { if (isAccessDenied(categories.error)) onAccessLost() }, [categories.error, onAccessLost])
  return <details onToggle={(event) => setOpened(event.currentTarget.open)}><summary>Категория: {selectedName}</summary>
    <p>Выбор относится к одной категории. Вложенные категории можно найти отдельно.</p>
    <form onSubmit={(event) => { event.preventDefault(); setSearch(input.trim()); setPage(0) }}>
      <label htmlFor="portal-category-search">Найти категорию</label>
      <Input id="portal-category-search" maxLength={160} value={input} onChange={(event) => setInput(event.target.value)} />
      <Button type="submit">Найти категорию</Button>
    </form>
    <Button onClick={() => onSelect(undefined)}>Все категории</Button>
    {categories.isError ? <p role="alert">Категории не загружены. <Button onClick={() => void categories.refetch()}>Повторить</Button></p>
      : categories.isPending ? <p>Загружаем категории…</p> : <>
        <ul>{opened && categories.data.content.map((category) => <CategoryOption key={category.id} id={category.id}
          token={token} identity={identity} onSelect={onSelect} onAccessLost={onAccessLost} />)}</ul>
        {categories.data.totalPages > 1 && <nav aria-label="Страницы категорий"><Button disabled={page === 0} onClick={() => setPage(page - 1)}>Категории: назад</Button>
        <span> {page + 1} / {Math.max(1, categories.data.totalPages)} </span>
        <Button disabled={page + 1 >= categories.data.totalPages} onClick={() => setPage(page + 1)}>Категории: далее</Button></nav>}
      </>}
  </details>
}

function CatalogItem({ item, busy, onAdd, productHref, quantity, onQuantity }: {
  item: PortalCatalogItem; busy: boolean; onAdd: (quantity: string) => void; productHref: string
  quantity: string; onQuantity: (value: string) => void
}) {
  const id = `portal-catalog-quantity-${item.skuId}`
  return <Card padding="lg"><article><h2><Link to={productHref}>{item.name}</Link></h2>
    {item.article && <p>Артикул: {item.article}</p>}
    <p>Единица: {item.unitName?.trim() || 'Название не передано из 1С'}</p>
    {item.description && <details><summary>Описание товара</summary><p>{item.description}</p></details>}
    <dl><dt>Цена за базовую единицу</dt><dd>{item.price === null ? 'Не определена' : `${formatDecimal(item.price)} ${item.currency ?? ''}`}</dd>
      <dt>Доступный остаток</dt><dd>{item.availableQuantity === null ? 'Неизвестен' : formatDecimal(item.availableQuantity)}</dd></dl>
    <details><summary>Единица и актуальность данных</summary><dl>
      <dt>Код единицы из 1С</dt><dd>{item.unitCode ?? 'Не указан'}</dd>
      <dt>Остаток получен</dt><dd>{item.stockReceivedAt ? new Date(item.stockReceivedAt).toLocaleString('ru-RU') : 'Нет данных'}</dd>
      <dt>Чтение источника начато</dt><dd>{item.stockCaptureStartedAt
        ? new Date(item.stockCaptureStartedAt).toLocaleString('ru-RU') : 'Время чтения не подтверждено'}</dd></dl></details>
    {item.availabilityReasons.length > 0 && <ul className="portal-catalog-reasons">{item.availabilityReasons.map((reason, index) =>
      <li key={`${reason.code}:${index}`}>{reason.message}</li>)}</ul>}
    <form onSubmit={(event) => { event.preventDefault(); if (!busy && item.canAddToCart) onAdd(quantity.replace(',', '.')) }}>
      <label htmlFor={id}>Количество в базовых единицах</label>
      <Input id={id} required inputMode="decimal" pattern="[0-9]+([.,][0-9]{1,3})?" maxLength={19}
        value={quantity} disabled={busy || !item.canAddToCart} onChange={(event) => onQuantity(event.target.value)} />
      <Button type="submit" variant="primary" disabled={busy || !item.canAddToCart}>Добавить в корзину</Button>
    </form>
  </article></Card>
}

export function formatDecimal(value: string) {
  const [integer = '', fraction] = value.split('.')
  return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')}${fraction === undefined ? '' : `,${fraction}`}`
}

function isAccessDenied(error: unknown) {
  return error instanceof ApiError && [401, 403, 404].includes(error.status)
}
